import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Typography, CircularProgress, Alert,
  LinearProgress, Divider, IconButton, Tooltip, Button,
  Menu, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  People as PeopleIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Cell, Legend
} from 'recharts';
import * as XLSX from 'xlsx';
import { siteAPI, dashboardAPI } from '../services/api';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtCr = (n) => {
  if (!n || n === 0) return '₹0';
  const num = parseFloat(n);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const fmtNum = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function ExecutivePage() {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [stats, setStats] = useState(null);
  const [finalBoqs, setFinalBoqs] = useState([]);
  const [siteEntries, setSiteEntries] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [downloadAnchor, setDownloadAnchor] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sitesRes, statsRes, boqRes] = await Promise.all([
        siteAPI.getAll(),
        dashboardAPI.getStats(),
        fetch(`${API_BASE_URL}/boqs/unified-summary`, authHeaders()).then(r => r.json()).catch(() => ({ data: { boqs: [] } }))
      ]);
      const sitesData = sitesRes.data.data.sites || [];
      setSites(sitesData);
      setStats(statsRes.data.data);
      const boqs = boqRes.data?.boqs || [];
      setFinalBoqs(boqs.filter(b => b.status === 'final'));

      // Fetch entry counts per site
      const entryCounts = {};
      await Promise.all(sitesData.slice(0, 10).map(async (site) => {
        try {
          const res = await axios.get(`${API_BASE_URL}/materials/site/${site.id}`, authHeaders());
          const entries = res.data.data?.entries || [];
          entryCounts[site.id] = {
            total: entries.length,
            received: entries.filter(e => e.entryType === 'received').length,
            totalSpent: entries.filter(e => e.entryType === 'received').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
          };
        } catch (e) { entryCounts[site.id] = { total: 0, received: 0, totalSpent: 0 }; }
      }));
      setSiteEntries(entryCounts);
      setLastUpdated(new Date());
    } catch (e) {
      setError('Failed to load executive data');
    } finally {
      setLoading(false);
    }
  };

  const handleExcelDownload = () => {
    const wb = XLSX.utils.book_new();

    // Sheet 1: Portfolio Summary
    const summaryData = [
      ['PAANAP — Executive Portfolio Report'],
      ['Generated:', new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })],
      [''],
      ['PORTFOLIO SUMMARY'],
      ['Total Portfolio Value', fmtNum(totalProjectValue)],
      ['Total Budget Allocated', fmtNum(totalBudget)],
      ['Total Expenditure', fmtNum(totalSpending)],
      ['Total Remaining', fmtNum(totalBudget - totalSpending)],
      ['Budget Compliance Rate', `${budgetComplianceRate}%`],
      ['Active Sites', activeSites.length],
      ['Sites Over Budget', overBudgetSites.length],
      ['Final BOQs', finalBoqs.length],
      [''],
      ['SITE-WISE DETAILS'],
      ['Site Name', 'Location', 'Client', 'Project Code', 'Status', 'Project Value', 'Budget', 'Spent', 'Remaining', 'Used %', 'Budget Status', 'Supervisors', 'Material Entries', 'Final BOQ', 'Start Date'],
      ...sites.map(site => {
        const budget = parseFloat(site.projectedBudget) || 0;
        const spent = parseFloat(site.actualSpending) || 0;
        const pct = budget > 0 ? ((spent / budget) * 100).toFixed(1) : 'N/A';
        const status = budget > 0 ? (spent >= budget ? 'Over Budget' : spent / budget >= (site.budgetAlertThreshold || 80) / 100 ? 'Near Limit' : 'On Track') : 'No Budget';
        const finalBoq = finalBoqs.find(b => b.site?.id === site.id || b.siteId === site.id);
        const entries = siteEntries[site.id];
        return [
          site.siteName,
          site.location,
          site.clientName || '-',
          site.projectCode || '-',
          site.status,
          parseFloat(site.projectValue) || 0,
          budget,
          spent,
          budget - spent,
          pct !== 'N/A' ? `${pct}%` : 'N/A',
          status,
          site.assignedUsers?.map(u => u.name).join(', ') || 'None',
          entries?.received || 0,
          finalBoq ? `${finalBoq.versionLabel} (Approved)` : 'Not Set',
          site.startDate ? new Date(site.startDate).toLocaleDateString('en-IN') : '-'
        ];
      })
    ];

    const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
    ws1['!cols'] = [{ wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 15 }, { wch: 25 }, { wch: 18 }, { wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, ws1, 'Portfolio Summary');

    // Sheet 2: Budget Analysis
    const budgetData = [
      ['BUDGET ANALYSIS'],
      ['Site Name', 'Project Value', 'Budget', 'Spent', 'Remaining', 'Used %', 'Alert Threshold', 'Status'],
      ...sites.filter(s => parseFloat(s.projectedBudget) > 0).map(site => {
        const budget = parseFloat(site.projectedBudget);
        const spent = parseFloat(site.actualSpending) || 0;
        const pct = (spent / budget) * 100;
        return [
          site.siteName,
          parseFloat(site.projectValue) || 0,
          budget,
          spent,
          budget - spent,
          `${pct.toFixed(1)}%`,
          `${site.budgetAlertThreshold || 80}%`,
          pct >= 100 ? 'OVER BUDGET' : pct >= (site.budgetAlertThreshold || 80) ? 'NEAR LIMIT' : 'ON TRACK'
        ];
      })
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(budgetData);
    ws2['!cols'] = [{ wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 10 }, { wch: 15 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(wb, ws2, 'Budget Analysis');

    XLSX.writeFile(wb, `Paanap_Executive_Portfolio_${new Date().toISOString().split('T')[0]}.xlsx`);
    setDownloadAnchor(null);
  };

  const handlePrint = () => {
    window.print();
    setDownloadAnchor(null);
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
      <CircularProgress sx={{ color: '#64A8ED' }} />
    </Box>
  );

  if (error) return <Alert severity="error">{error}</Alert>;

  // Metrics
  const totalProjectValue = sites.reduce((s, site) => s + (parseFloat(site.projectValue) || 0), 0);
  const totalBudget = sites.reduce((s, site) => s + (parseFloat(site.projectedBudget) || 0), 0);
  const totalSpending = sites.reduce((s, site) => s + (parseFloat(site.actualSpending) || 0), 0);
  const activeSites = sites.filter(s => s.status === 'active' || s.status === 'in_progress');
  const overBudgetSites = sites.filter(s => {
    const b = parseFloat(s.projectedBudget) || 0;
    const sp = parseFloat(s.actualSpending) || 0;
    return b > 0 && sp >= b;
  });
  const sitesWithBudget = sites.filter(s => parseFloat(s.projectedBudget) > 0);
  const onTrackSites = sitesWithBudget.filter(s => {
    const b = parseFloat(s.projectedBudget);
    const sp = parseFloat(s.actualSpending) || 0;
    return (sp / b) * 100 < (parseFloat(s.budgetAlertThreshold) || 80);
  });
  const budgetComplianceRate = sitesWithBudget.length > 0
    ? Math.round((onTrackSites.length / sitesWithBudget.length) * 100)
    : 100;

  // Chart data
  const chartData = sitesWithBudget.map(s => ({
    name: s.siteName.length > 12 ? s.siteName.substring(0, 12) + '…' : s.siteName,
    fullName: s.siteName,
    budget: parseFloat(s.projectedBudget) / 100000,
    spent: parseFloat(s.actualSpending || 0) / 100000,
    isOver: parseFloat(s.actualSpending || 0) >= parseFloat(s.projectedBudget)
  }));

  const MetricCard = ({ label, value, sub, color, bg, icon: Icon, border }) => (
    <Box sx={{ p: 2.5, background: bg || 'white', borderRadius: '16px', border: `1px solid ${border || '#e2e8f0'}`, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>{label}</Typography>
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: color || '#0f172a', fontFamily: 'Plus Jakarta Sans, sans-serif', lineHeight: 1.1 }}>{value}</Typography>
          {sub && <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.5 }}>{sub}</Typography>}
        </Box>
        {Icon && <Box sx={{ p: 1.5, borderRadius: '12px', background: `${color}18` }}><Icon sx={{ fontSize: 26, color }} /></Box>}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ background: '#f8fafc', minHeight: '100vh', fontFamily: 'Plus Jakarta Sans, sans-serif' }} id="executive-print">
      <style>{`
       @media print {
       .no-print { display: none !important; }
       nav, aside, header { display: none !important; }
       [class*="MuiDrawer"] { display: none !important; }
       [class*="MuiAppBar"] { display: none !important; }
       main { margin: 0 !important; padding: 0 !important; width: 100% !important; }
       #executive-print { background: white !important; }
       body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
       .site-card { page-break-inside: avoid !important; break-inside: avoid !important; }
       .portfolio-table { page-break-inside: avoid !important; break-inside: avoid !important; }
      }
      `}</style>

      {/* ── Hero Header ── */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #0f2044 100%)', px: { xs: 3, md: 6 }, pt: 5, pb: 6, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle at 25px 25px, white 2px, transparent 0)', backgroundSize: '50px 50px' }} />
        <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(100,168,237,0.15) 0%, transparent 70%)' }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, position: 'relative' }}>
          <IconButton className="no-print" onClick={() => navigate('/dashboard')} sx={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.08)', '&:hover': { background: 'rgba(255,255,255,0.15)' } }}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }} className="no-print">
            <Tooltip title="Refresh"><IconButton onClick={fetchAll} sx={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.08)', '&:hover': { background: 'rgba(255,255,255,0.15)' } }}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={(e) => setDownloadAnchor(e.currentTarget)}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.5)' }, textTransform: 'none', fontWeight: 700, borderRadius: '10px' }}>
              Download Report
            </Button>
            <Menu anchorEl={downloadAnchor} open={Boolean(downloadAnchor)} onClose={() => setDownloadAnchor(null)}>
              <MenuItem onClick={handleExcelDownload}><TableChartIcon sx={{ mr: 1.5, fontSize: 18, color: '#16a34a' }} />Download Excel</MenuItem>
              <MenuItem onClick={handlePrint}><PrintIcon sx={{ mr: 1.5, fontSize: 18, color: '#2563eb' }} />Print / Save as PDF</MenuItem>
            </Menu>
            <Box sx={{ px: 2, py: 0.75, borderRadius: '8px', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)' }}>
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                {lastUpdated.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'linear-gradient(135deg,#64A8ED,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 15px rgba(100,168,237,0.4)' }}>
              <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.4rem' }}>P</Typography>
            </Box>
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '1.3rem', letterSpacing: '-0.02em' }}>PAANAP</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Connecting Power. Empowering Growth.</Typography>
            </Box>
          </Box>
          <Typography sx={{ color: 'white', fontWeight: 900, fontSize: { xs: '1.8rem', md: '2.6rem' }, letterSpacing: '-0.03em', lineHeight: 1.1, mb: 1 }}>Executive Portfolio</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '1rem', fontWeight: 500, mb: 4 }}>Comprehensive overview of all active infrastructure projects</Typography>
          <Grid container spacing={2}>
            {[
              { label: 'Total Portfolio Value', value: fmtCr(totalProjectValue), color: '#64A8ED' },
              { label: 'Total Budget Allocated', value: fmtCr(totalBudget), color: '#22d3ee' },
              { label: 'Total Expenditure', value: fmtCr(totalSpending), color: '#f59e0b' },
              { label: 'Budget Compliance', value: `${budgetComplianceRate}%`, color: budgetComplianceRate >= 80 ? '#4ade80' : budgetComplianceRate >= 50 ? '#fbbf24' : '#f87171' },
            ].map(({ label, value, color }) => (
              <Grid item xs={6} md={3} key={label}>
                <Box sx={{ p: 2.5, background: 'rgba(255,255,255,0.06)', borderRadius: '14px', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>{label}</Typography>
                  <Typography sx={{ fontSize: { xs: '1.2rem', md: '1.6rem' }, fontWeight: 900, color, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>

        {/* Portfolio Health */}
        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}><MetricCard label="Active Sites" value={activeSites.length} sub={`of ${sites.length} total sites`} color="#2563eb" icon={AccountBalanceIcon} /></Grid>
          <Grid item xs={6} md={3}><MetricCard label="On Track" value={onTrackSites.length} sub="within budget threshold" color="#16a34a" icon={CheckCircleIcon} /></Grid>
          <Grid item xs={6} md={3}><MetricCard label="Need Attention" value={overBudgetSites.length} sub="sites over budget" color={overBudgetSites.length > 0 ? '#dc2626' : '#16a34a'} icon={overBudgetSites.length > 0 ? WarningIcon : CheckCircleIcon} border={overBudgetSites.length > 0 ? '#fecaca' : '#e2e8f0'} bg={overBudgetSites.length > 0 ? '#fef2f2' : 'white'} /></Grid>
          <Grid item xs={6} md={3}><MetricCard label="Final BOQs" value={finalBoqs.length} sub="approved & locked" color="#d97706" icon={StarIcon} /></Grid>
        </Grid>

        {/* Budget vs Spending Chart */}
        {chartData.length > 0 && (
          <Box sx={{ background: 'white', borderRadius: '18px', border: '1px solid #e2e8f0', p: 3, mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a' }}>Budget vs Expenditure — All Sites</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', mt: 0.3 }}>Values in Lakhs (₹) · Red bar = over budget</Typography>
              </Box>
            </Box>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} barGap={4} barCategoryGap="30%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="name" style={{ fontSize: '0.72rem' }} axisLine={false} tickLine={false} />
                <YAxis style={{ fontSize: '0.72rem' }} axisLine={false} tickLine={false} tickFormatter={v => `₹${v}L`} />
                <RTooltip formatter={(v, n) => [`₹${(v * 100000).toLocaleString('en-IN')}`, n === 'budget' ? 'Budget' : 'Spent']} labelFormatter={(l) => chartData.find(d => d.name === l)?.fullName || l} contentStyle={{ borderRadius: 10, border: '1px solid #e2e8f0', fontSize: '0.82rem' }} />
                <Legend formatter={v => v === 'budget' ? 'Budget' : 'Spent'} />
                <Bar dataKey="budget" fill="#64A8ED" radius={[6, 6, 0, 0]} name="budget" />
                <Bar dataKey="spent" radius={[6, 6, 0, 0]} name="spent">
                  {chartData.map((e, i) => <Cell key={i} fill={e.isOver ? '#dc2626' : '#0f172a'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}

        {/* Project Portfolio — Detailed Cards */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', mb: 0.5 }}>Project Portfolio</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>{sites.length} infrastructure projects · Click any card for full details</Typography>
        </Box>

        <Grid container spacing={2.5} sx={{ mb: 4 }}>
          {sites.map(site => {
            const budget = parseFloat(site.projectedBudget) || 0;
            const spent = parseFloat(site.actualSpending) || 0;
            const projectValue = parseFloat(site.projectValue) || 0;
            const pct = budget > 0 ? (spent / budget) * 100 : 0;
            const isOver = budget > 0 && spent >= budget;
            const isNear = budget > 0 && pct >= (site.budgetAlertThreshold || 80) && !isOver;
            const barColor = isOver ? '#dc2626' : isNear ? '#d97706' : '#16a34a';
            const finalBoq = finalBoqs.find(b => b.site?.id === site.id || b.siteId === site.id);
            const statusColor = site.status === 'active' ? '#16a34a' : site.status === 'on_hold' ? '#d97706' : '#64748b';
            const entries = siteEntries[site.id];

            return (
              <Grid item xs={12} md={6} lg={4} key={site.id}>
                <Box className="site-card" sx={{ background: 'white', borderRadius: '18px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', transition: 'all 0.2s', cursor: 'pointer', '&:hover': { boxShadow: '0 8px 30px rgba(0,0,0,0.10)', transform: 'translateY(-2px)' } }}
                  onClick={() => navigate(`/sites/${site.id}`)}>
                  <Box sx={{ height: 4, background: isOver ? 'linear-gradient(90deg,#dc2626,#ef4444)' : isNear ? 'linear-gradient(90deg,#d97706,#f59e0b)' : 'linear-gradient(90deg,#64A8ED,#3b82f6)' }} />
                  <Box sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1, mr: 1 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#0f172a', lineHeight: 1.2, mb: 0.3 }}>{site.siteName}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>📍 {site.location}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: `${statusColor}15`, color: statusColor, border: `1px solid ${statusColor}30`, textTransform: 'uppercase' }}>
                          {site.status?.replace('_', ' ')}
                        </span>
                        {finalBoq && <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: 99, background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}>⭐ Final BOQ</span>}
                      </Box>
                    </Box>

                    {/* Project Info Grid */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2 }}>
                      {[
                        ['Client', site.clientName || '-'],
                        ['Project Code', site.projectCode || '-'],
                        ['Start Date', site.startDate ? new Date(site.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'],
                        ['Supervisors', site.assignedUsers?.length > 0 ? `${site.assignedUsers.length} assigned` : 'None assigned'],
                      ].map(([label, value]) => (
                        <Box key={label} sx={{ p: 1.25, background: '#f8fafc', borderRadius: '8px' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.25 }}>{label}</Typography>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>{value}</Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Financial Summary */}
                    {budget > 0 ? (
                      <>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          {[
                            ['Project Value', fmtCr(projectValue), '#2563eb'],
                            ['Budget', fmtCr(budget), '#0891b2'],
                            ['Spent', fmtCr(spent), barColor],
                            ['Remaining', fmtCr(budget - spent), budget - spent < 0 ? '#dc2626' : '#16a34a'],
                          ].map(([label, value, color]) => (
                            <Grid item xs={6} key={label}>
                              <Box sx={{ p: 1.25, background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.25 }}>{label}</Typography>
                                <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, color, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                        <Box sx={{ mb: 1.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>Budget Utilization</Typography>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: barColor }}>{pct.toFixed(1)}%</Typography>
                          </Box>
                          <LinearProgress variant="determinate" value={Math.min(pct, 100)}
                            sx={{ height: 7, borderRadius: 4, background: '#f1f5f9', '& .MuiLinearProgress-bar': { background: barColor, borderRadius: 4 } }} />
                          {isOver && <Typography sx={{ fontSize: '0.68rem', color: '#dc2626', mt: 0.5, fontWeight: 600 }}>🚨 Over by {(pct - 100).toFixed(1)}%</Typography>}
                        </Box>
                      </>
                    ) : (
                      <Box sx={{ py: 1.5, textAlign: 'center', background: '#f8fafc', borderRadius: '8px', mb: 1.5 }}>
                        <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>No budget set</Typography>
                      </Box>
                    )}

                    {/* Footer Stats */}
                    <Divider sx={{ mb: 1.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PeopleIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                          <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>{site.assignedUsers?.length || 0} supervisor{site.assignedUsers?.length !== 1 ? 's' : ''}</Typography>
                        </Box>
                        {entries && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AssessmentIcon sx={{ fontSize: 13, color: '#94a3b8' }} />
                            <Typography sx={{ fontSize: '0.72rem', color: '#64748b' }}>{entries.received} entries</Typography>
                          </Box>
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 600 }}>
                        {finalBoq ? `BOQ: ${finalBoq.versionLabel}` : 'No Final BOQ'}
                      </Typography>
                    </Box>

                    {/* Supervisor names */}
                    {site.assignedUsers?.length > 0 && (
                      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {site.assignedUsers.map(u => (
                          <span key={u.id} style={{ fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99, background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>{u.name}</span>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Detailed Summary Table */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', mb: 0.5 }}>Portfolio Summary Table</Typography>
          <Typography sx={{ fontSize: '0.82rem', color: '#94a3b8' }}>Consolidated view of all sites</Typography>
        </Box>
        <Paper sx={{ borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden', mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: '#0f172a' }}>
                  {['Site Name', 'Location', 'Client', 'Status', 'Project Value', 'Budget', 'Spent', 'Remaining', 'Used %', 'BOQ Status', 'Team'].map(h => (
                    <TableCell key={h} sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 700, fontSize: '0.72rem', py: 1.5, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sites.map((site, i) => {
                  const budget = parseFloat(site.projectedBudget) || 0;
                  const spent = parseFloat(site.actualSpending) || 0;
                  const pct = budget > 0 ? (spent / budget) * 100 : null;
                  const isOver = budget > 0 && spent >= budget;
                  const isNear = budget > 0 && pct >= (site.budgetAlertThreshold || 80) && !isOver;
                  const finalBoq = finalBoqs.find(b => b.site?.id === site.id || b.siteId === site.id);
                  return (
                    <TableRow key={site.id} hover sx={{ background: i % 2 === 0 ? 'white' : '#fafafa', cursor: 'pointer', '&:hover': { background: '#eff6ff' } }} onClick={() => navigate(`/sites/${site.id}`)}>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#0f172a' }}>{site.siteName}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: '#64748b' }}>{site.location}</TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{site.clientName || '-'}</TableCell>
                      <TableCell>
                        <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: site.status === 'active' ? '#f0fdf4' : '#f8fafc', color: site.status === 'active' ? '#16a34a' : '#64748b', border: `1px solid ${site.status === 'active' ? '#bbf7d0' : '#e2e8f0'}` }}>
                          {site.status?.replace('_', ' ')}
                        </span>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#2563eb' }}>{fmtCr(site.projectValue)}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem' }}>{budget > 0 ? fmtCr(budget) : '-'}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', fontWeight: 600, color: isOver ? '#dc2626' : isNear ? '#d97706' : '#0f172a' }}>{fmtCr(spent)}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', color: budget - spent < 0 ? '#dc2626' : '#16a34a', fontWeight: 600 }}>{budget > 0 ? fmtCr(budget - spent) : '-'}</TableCell>
                      <TableCell>
                        {pct !== null ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 80 }}>
                            <LinearProgress variant="determinate" value={Math.min(pct, 100)} sx={{ flex: 1, height: 5, borderRadius: 3, background: '#f1f5f9', '& .MuiLinearProgress-bar': { background: isOver ? '#dc2626' : isNear ? '#d97706' : '#16a34a', borderRadius: 3 } }} />
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: isOver ? '#dc2626' : isNear ? '#d97706' : '#16a34a', minWidth: 36 }}>{pct.toFixed(0)}%</Typography>
                          </Box>
                        ) : <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>No budget</Typography>}
                      </TableCell>
                      <TableCell>
                        {finalBoq ? (
                          <span style={{ fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: '#fffbeb', color: '#d97706', border: '1px solid #fde68a' }}>⭐ {finalBoq.versionLabel}</span>
                        ) : (
                          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>Pending</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem' }}>{site.assignedUsers?.map(u => u.name).join(', ') || '-'}</TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals Row */}
                <TableRow sx={{ background: '#0f172a' }}>
                  <TableCell colSpan={4} sx={{ color: 'white', fontWeight: 800, fontSize: '0.82rem', py: 1.5 }}>PORTFOLIO TOTAL</TableCell>
                  <TableCell sx={{ color: '#64A8ED', fontWeight: 800, fontSize: '0.85rem' }}>{fmtCr(totalProjectValue)}</TableCell>
                  <TableCell sx={{ color: '#22d3ee', fontWeight: 800, fontSize: '0.85rem' }}>{fmtCr(totalBudget)}</TableCell>
                  <TableCell sx={{ color: '#f59e0b', fontWeight: 800, fontSize: '0.85rem' }}>{fmtCr(totalSpending)}</TableCell>
                  <TableCell sx={{ color: totalBudget - totalSpending < 0 ? '#f87171' : '#4ade80', fontWeight: 800, fontSize: '0.85rem' }}>{fmtCr(totalBudget - totalSpending)}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '0.82rem' }}>{budgetComplianceRate}% compliance</TableCell>
                  <TableCell colSpan={2} sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>{finalBoqs.length} final BOQs across portfolio</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Footer */}
        <Box sx={{ pt: 3, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: 'linear-gradient(135deg,#64A8ED,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ color: 'white', fontWeight: 900, fontSize: '0.9rem' }}>P</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', color: '#0f172a' }}>PAANAP</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>Connecting Power. Empowering Growth.</Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>
            Confidential — Authorized Use Only · {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}